/**
 * L6 — bandeau global de recouvrement.
 *
 * Règles (SUPERADMIN_PLAN.md §6) :
 *  - Licence perpetual/internal : aucun bandeau, jamais. AMA doit voir
 *    exactement zéro rappel de facturation.
 *  - past_due    : bandeau ambre "N factures impayées". Dismissible pour
 *                  la journée (localStorage clé quotidienne).
 *  - restricted  : bandeau orange "lecture seule". Non dismissible.
 *  - suspended   : redirige vers /suspended (page dédiée qui prend le
 *                  relais). Le bandeau lui-même ne s'affiche pas.
 */

import { useSubscription, useMyInvoices } from '@/hooks/useSubscription'
import { useAuthStore } from '@/stores/auth.store'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { AlertOctagon, AlertTriangle, X } from 'lucide-react'
import { useEffect, useState } from 'react'

function todayKey(): string {
  const now = new Date()
  return `dunning-banner-dismissed-${now.toISOString().slice(0, 10)}`
}

function fmtXOF(n: number | undefined): string {
  if (!n) return '0'
  return new Intl.NumberFormat('fr-FR').format(n)
}

export function DunningBanner() {
  const { data } = useSubscription()
  const invoicesQ = useMyInvoices('past_due')
  const navigate = useNavigate()
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      setDismissed(window.localStorage.getItem(todayKey()) === '1')
    } catch {
      setDismissed(false)
    }
  }, [])

  const license = data?.license
  // Kind = perpetual/internal → jamais de bandeau.
  if (!license || license.kind === 'perpetual' || license.kind === 'internal') return null

  const level = license.dunning_level

  // L5a — l'owner du tenant garde l'accès complet même en suspended
  // (règle §2.4 n°1). Il voit un bandeau rouge à la place de la
  // redirection forcée vers /suspended.
  const isOwner = (useAuthStore.getState().user as { is_owner?: boolean } | null)?.is_owner === true

  // Suspended → redirection vers la page dédiée. Ne pas rediriger si on
  // est déjà sur /suspended ou dans /settings/subscription (l'owner doit
  // pouvoir régler). Owner : jamais de redirection.
  useEffect(() => {
    if (level !== 'suspended' || isOwner) return
    const allowed =
      pathname === '/suspended' ||
      pathname.startsWith('/settings/subscription') ||
      pathname.startsWith('/settings/profile')
    if (!allowed) {
      navigate({ to: '/suspended' })
    }
  }, [level, pathname, navigate, isOwner])

  // Owner en suspended → bandeau rouge permanent (jamais dismissible)
  if (level === 'suspended') {
    if (!isOwner) return null
    return (
      <div className="flex items-center gap-3 border-b border-red-700 bg-red-600 px-4 py-2 text-sm text-white">
        <span className="font-semibold">Abonnement suspendu</span>
        <span className="opacity-90">
          Vos équipes n'ont plus accès à l'application. Régularisez pour rétablir le service.
        </span>
      </div>
    )
  }
  if (level !== 'past_due' && level !== 'restricted') return null

  // past_due : dismissible pour la journée.
  if (level === 'past_due' && dismissed) return null

  const invoices = invoicesQ.data ?? []
  const overdueCount = invoices.length
  const overdueTotal = invoices.reduce((sum, inv) => sum + inv.total_xof, 0)

  const styles =
    level === 'past_due'
      ? {
          Icon: AlertTriangle,
          className:
            'border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/70 dark:text-amber-100',
          title:
            overdueCount > 0
              ? `Vous avez ${overdueCount} facture${overdueCount > 1 ? 's' : ''} impayée${overdueCount > 1 ? 's' : ''} — total ${fmtXOF(overdueTotal)} FCFA.`
              : 'Votre abonnement présente une facture en retard de paiement.',
          body:
            'Régularisez votre situation pour éviter la mise en lecture seule de votre compte.',
          canDismiss: true,
        }
      : {
          Icon: AlertOctagon,
          className:
            'border-orange-300 bg-orange-50 text-orange-900 dark:border-orange-900/60 dark:bg-orange-950/70 dark:text-orange-100',
          title: 'Compte en lecture seule',
          body:
            'Certaines fonctionnalités sont désactivées suite à un impayé. Régularisez pour rétablir un accès complet.',
          canDismiss: false,
        }

  const onDismiss = () => {
    try {
      window.localStorage.setItem(todayKey(), '1')
    } catch {
      /* ignore */
    }
    setDismissed(true)
  }

  const Icon = styles.Icon

  return (
    <div role="alert" className={`w-full border-b px-4 py-2 md:px-6 lg:px-8 ${styles.className}`}>
      <div className="mx-auto flex w-full max-w-7xl items-start gap-3">
        <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <div className="flex-1">
          <p className="text-sm font-semibold">{styles.title}</p>
          <p className="text-xs opacity-90">{styles.body}</p>
        </div>
        <Link
          to="/settings/subscription"
          className="inline-flex min-h-[36px] items-center rounded-md border border-current bg-transparent px-3 py-1 text-xs font-semibold transition-colors hover:bg-black/5 dark:hover:bg-white/10"
        >
          {level === 'past_due' ? 'Voir mon abonnement' : 'Régler'}
        </Link>
        {styles.canDismiss ? (
          <button
            type="button"
            onClick={onDismiss}
            aria-label="Fermer"
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-colors hover:bg-black/5 dark:hover:bg-white/10"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        ) : null}
      </div>
    </div>
  )
}
